import { useState, useRef } from "react";
import {
    Check, User, AlertCircle, Upload, Camera, ShieldCheck, Image as ImageIcon,
} from "lucide-react";

/* ---------------------------------------------------------
   Design tokens
--------------------------------------------------------- */
const T = {
    navy: "#14213D",
    paper: "#F3F1EA",
    card: "#FFFFFF",
    border: "#E1DCCF",
    saffron: "#C97D2E",
    saffronSoft: "#F3E2CC",
    green: "#2F6B4F",
    greenSoft: "#E3EEE7",
    red: "#B3413B",
    redSoft: "#F6E4E2",
    slate: "#6B7280",
    ink: "#1F2430",
};

const SERIF = "'Source Serif 4', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Courier New', monospace";

/* ---------------------------------------------------------
   Shared field primitives
--------------------------------------------------------- */
function Field({ label, hint, error, children, innerRef }) {
    return (
        <div ref={innerRef} style={{ marginBottom: "22px" }}>
            {label && (
                <label
                    style={{
                        display: "block", fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                        letterSpacing: "0.04em", textTransform: "uppercase", color: T.navy, marginBottom: "7px",
                    }}
                >
                    {label}
                </label>
            )}
            {children}
            {hint && !error && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: T.slate, marginTop: "6px" }}>{hint}</div>
            )}
            {error && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: T.red, marginTop: "6px", display: "flex", alignItems: "center", gap: "5px" }}>
                    <AlertCircle size={13} /> {error}
                </div>
            )}
        </div>
    );
}

function Row({ children }) {
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
            }}
        >
            {children}
        </div>
    );
}

function inputStyle(error, mono) {
    return {
        width: "100%",
        boxSizing: "border-box",
        padding: "11px 13px",
        borderRadius: "6px",
        border: `1.5px solid ${error ? T.red : T.border}`,
        background: T.paper,
        fontFamily: mono ? MONO : SANS,
        fontSize: "14.5px",
        letterSpacing: mono ? "0.04em" : "normal",
        color: T.ink,
        outline: "none",
    };
}

function TextInput({ error, mono, ...rest }) {
    return <input {...rest} style={inputStyle(error, mono)} />;
}

function SelectInput({ error, children, ...rest }) {
    return (
        <select {...rest} style={{ ...inputStyle(error, false), appearance: "auto" }}>
            {children}
        </select>
    );
}

function FileUpload({ file, onChange, error, id, label }) {
    const fmtSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };
    return (
        <div>
            <input
                id={id}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => onChange(e.target.files?.[0] || null)}
                style={{ display: "none" }}
            />
            <label
                htmlFor={id}
                style={{
                    display: "flex", alignItems: "center", gap: "12px",
                    border: `1.5px dashed ${error ? T.red : file ? T.green : T.border}`,
                    borderRadius: "8px", padding: "13px 15px",
                    background: file ? T.greenSoft : T.paper, cursor: "pointer",
                }}
            >
                <div
                    style={{
                        width: "30px", height: "30px", borderRadius: "6px",
                        background: file ? "rgba(47,107,79,0.15)" : T.saffronSoft,
                        color: file ? T.green : T.saffron,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                >
                    <Upload size={15} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {file ? (
                        <>
                            <div style={{ fontFamily: SANS, fontSize: "13px", fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {file.name}
                            </div>
                            <div style={{ fontFamily: SANS, fontSize: "11.5px", color: T.slate }}>{fmtSize(file.size)} · tap to replace</div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontFamily: SANS, fontSize: "13px", fontWeight: 600, color: T.ink }}>{label || "Upload a clear photo or scan"}</div>
                            <div style={{ fontFamily: SANS, fontSize: "11.5px", color: T.slate }}>JPG, PNG or PDF · up to 5 MB</div>
                        </>
                    )}
                </div>
                {file && <Check size={16} color={T.green} strokeWidth={3} />}
            </label>
            {error && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: T.red, marginTop: "6px", display: "flex", alignItems: "center", gap: "5px" }}>
                    <AlertCircle size={13} /> {error}
                </div>
            )}
        </div>
    );
}

/* ---------------------------------------------------------
   Divider between groups of fields, inside the single box
--------------------------------------------------------- */
function Divider({ label }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "6px 0 22px" }}>
            <span
                style={{
                    fontFamily: SANS, fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.1em",
                    color: T.saffron, whiteSpace: "nowrap",
                }}
            >
                {label}
            </span>
            <div style={{ flex: 1, height: "1px", background: T.border }} />
        </div>
    );
}

function VerifiedPill() {
    return (
        <div
            style={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                fontFamily: SANS, fontSize: "11.5px", fontWeight: 700, color: T.green,
                background: T.greenSoft, padding: "4px 9px", borderRadius: "999px",
                letterSpacing: "0.03em", marginBottom: "10px",
            }}
        >
            <ShieldCheck size={13} /> LOOKS VALID
        </div>
    );
}

/* ---------------------------------------------------------
   Validation
--------------------------------------------------------- */
function validateAll(data) {
    const e = {};
    if (!data.fullName.trim()) e.fullName = "Enter your full name";
    if (!data.guardianName.trim()) e.guardianName = "Required";
    if (!data.dob) e.dob = "Select date of birth";
    if (!data.gender) e.gender = "Select an option";
    if (!/^\d{10}$/.test(data.mobile)) e.mobile = "Enter a valid 10-digit number";
    if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = "Enter a valid email";

    if (!/^\d{12}$/.test(data.aadhar)) e.aadhar = "Aadhar must be exactly 12 digits";
    if (!data.aadharFile) e.aadharFile = "Upload a copy of your Aadhar card";
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(data.pan)) e.pan = "Enter a valid PAN, e.g. ABCDE1234F";
    if (!data.panFile) e.panFile = "Upload a copy of your PAN card";
    if (data.gst && !/^\d{2}[A-Z0-9]{13}$/.test(data.gst)) e.gst = "Enter a valid 15-character GSTIN";

    if (!data.addr1.trim()) e.addr1 = "Required";
    if (!data.city.trim()) e.city = "Required";
    if (!data.state) e.state = "Select a state";
    if (!/^\d{6}$/.test(data.pincode)) e.pincode = "Enter a valid 6-digit PIN";

    return e;
}

const REQUIRED_KEYS = [
    "fullName", "guardianName", "dob", "gender", "mobile", "email",
    "aadhar", "aadharFile", "pan", "panFile",
    "addr1", "city", "state", "pincode",
];

const FIELD_ORDER = [
    "fullName", "guardianName", "dob", "gender", "mobile", "email",
    "aadhar", "aadharFile", "pan", "panFile", "gst",
    "addr1", "city", "state", "pincode",
];

/* ---------------------------------------------------------
   Main page — everything in ONE box, field after field
--------------------------------------------------------- */
export default function RegistrationForm() {
    const [errors, setErrors] = useState({});
    const [agreed, setAgreed] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [data, setData] = useState({
        fullName: "", guardianName: "", dob: "", gender: "", mobile: "", email: "",
        aadhar: "", aadharFile: null, pan: "", panFile: null, gst: "", gstFile: null,
        addr1: "", addr2: "", city: "", state: "", pincode: "",
        avatarPhoto: null,
    });

    const fieldRefs = useRef({});
    const setFieldRef = (key) => (el) => { fieldRefs.current[key] = el; };

    const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

    const aadharValid = /^\d{12}$/.test(data.aadhar);
    const panValid = /^[A-Z]{5}\d{4}[A-Z]$/.test(data.pan);
    const gstValid = data.gst === "" || /^\d{2}[A-Z0-9]{13}$/.test(data.gst);
    const formatAadhar = (v) => v.replace(/\D/g, "").slice(0, 12).replace(/(\d{4})(?=\d)/g, "$1 ");

    const handleAvatar = (file) => {
        update("avatarPhoto", file);
        setAvatarPreview(file ? URL.createObjectURL(file) : null);
    };

    const handleSubmit = () => {
        const e = validateAll(data);
        setErrors(e);
        if (Object.keys(e).length > 0) {
            const firstKey = FIELD_ORDER.find((k) => e[k]);
            fieldRefs.current[firstKey]?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        if (!agreed) return;
        setSubmitted(true);
    };

    const filled = REQUIRED_KEYS.filter((k) => data[k] !== "" && data[k] !== null).length;
    const pct = Math.round((filled / REQUIRED_KEYS.length) * 100);
    const errorCount = Object.keys(errors).length;

    if (submitted) {
        return (
            <div
                style={{
                    height: "100vh", overflowY: "auto", WebkitOverflowScrolling: "touch",
                    background: T.paper, display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "20px", fontFamily: SANS, boxSizing: "border-box",
                }}
            >
                <div style={{ textAlign: "center", maxWidth: "420px" }}>
                    <div
                        style={{
                            width: "68px", height: "68px", borderRadius: "50%",
                            border: `2.5px dashed ${T.green}`, background: T.greenSoft,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            margin: "0 auto 22px", transform: "rotate(-6deg)",
                        }}
                    >
                        <Check size={30} color={T.green} strokeWidth={3} />
                    </div>
                    <h1 style={{ fontFamily: SERIF, fontSize: "28px", color: T.navy, margin: "0 0 10px" }}>Registration submitted</h1>
                    <p style={{ fontFamily: SANS, fontSize: "14px", color: T.slate, lineHeight: 1.6, marginBottom: "26px" }}>
                        Thanks, {data.fullName.split(" ")[0] || "there"}. We've received your details and documents.
                        A confirmation will be sent to <strong style={{ color: T.ink }}>{data.email}</strong>.
                    </p>
                    <button
                        onClick={() => { setSubmitted(false); setAgreed(false); setErrors({}); }}
                        style={{
                            fontFamily: SANS, fontWeight: 700, fontSize: "13.5px", color: "#fff",
                            background: T.navy, border: "none", borderRadius: "7px", padding: "12px 22px", cursor: "pointer",
                        }}
                    >
                        Start a new registration
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                height: "100vh", overflowY: "auto", WebkitOverflowScrolling: "touch",
                background: T.paper, padding: "40px 20px 80px", fontFamily: SANS, boxSizing: "border-box",
            }}
        >
            <div style={{ maxWidth: "940px", margin: "0 auto" }}>

                {/* ---- ONE box holding everything ---- */}
                <div
                    style={{
                        background: T.card, borderRadius: "12px", border: `1px solid ${T.border}`,
                        padding: "34px 32px 30px",
                    }}
                >
                    {/* Title + completeness */}
                    <div style={{ marginBottom: "26px" }}>
                        <div style={{ fontFamily: SANS, fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.12em", color: T.saffron, marginBottom: "6px" }}>
                            REGISTRATION FORM
                        </div>
                        <h1 style={{ fontFamily: SERIF, fontSize: "26px", color: T.navy, margin: "0 0 14px 0" }}>Your details</h1>
                        <div style={{ height: "6px", borderRadius: "999px", background: T.paper, overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", borderRadius: "999px", background: pct === 100 ? T.green : T.saffron, transition: "width 0.25s ease" }} />
                        </div>
                        <div style={{ fontFamily: SANS, fontSize: "11.5px", color: T.slate, marginTop: "6px" }}>{pct}% complete</div>
                    </div>

                    {errorCount > 0 && (
                        <div
                            style={{
                                display: "flex", alignItems: "center", gap: "10px",
                                background: T.redSoft, border: `1px solid ${T.red}`, borderRadius: "8px",
                                padding: "12px 14px", marginBottom: "22px",
                            }}
                        >
                            <AlertCircle size={16} color={T.red} />
                            <span style={{ fontFamily: SANS, fontSize: "13px", color: T.red, fontWeight: 600 }}>
                                {errorCount} field{errorCount > 1 ? "s need" : " needs"} your attention.
                            </span>
                        </div>
                    )}

                    {/* Profile photo */}
                    <Field label="Profile photo">
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                            <div
                                style={{
                                    width: "72px", height: "72px", borderRadius: "50%",
                                    background: T.saffronSoft, color: T.saffron,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`,
                                }}
                            >
                                {avatarPreview ? (
                                    <img src={avatarPreview} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                    <User size={30} />
                                )}
                            </div>
                            <input
                                id="avatarFile"
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={(e) => handleAvatar(e.target.files?.[0] || null)}
                            />
                            <label
                                htmlFor="avatarFile"
                                style={{
                                    display: "flex", alignItems: "center", gap: "7px",
                                    fontFamily: SANS, fontSize: "13px", fontWeight: 700, color: T.navy,
                                    border: `1.5px solid ${T.border}`, borderRadius: "7px",
                                    padding: "9px 14px", cursor: "pointer", background: T.paper,
                                }}
                            >
                                <Camera size={14} /> {avatarPreview ? "Change photo" : "Upload photo"}
                            </label>
                        </div>
                    </Field>

                    <Divider label="PERSONAL INFORMATION" />

                    <Field label="Full name" error={errors.fullName} innerRef={setFieldRef("fullName")}>
                        <TextInput placeholder="e.g. Ananya Rao Deshmukh" value={data.fullName} onChange={(e) => update("fullName", e.target.value)} error={errors.fullName} />
                    </Field>
                    <Field label="Father's / Husband's name" error={errors.guardianName} innerRef={setFieldRef("guardianName")}>
                        <TextInput placeholder="e.g. Ramesh Rao Deshmukh" value={data.guardianName} onChange={(e) => update("guardianName", e.target.value)} error={errors.guardianName} />
                    </Field>
                    <Row>
                        <Field label="Date of birth" error={errors.dob} innerRef={setFieldRef("dob")}>
                            <TextInput type="date" value={data.dob} onChange={(e) => update("dob", e.target.value)} error={errors.dob} />
                        </Field>
                        <Field label="Gender" error={errors.gender} innerRef={setFieldRef("gender")}>
                            <SelectInput value={data.gender} onChange={(e) => update("gender", e.target.value)} error={errors.gender}>
                                <option value="">Select</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                                <option value="other">Other / prefer not to say</option>
                            </SelectInput>
                        </Field>
                    </Row>
                    <Row>
                        <Field label="Mobile number" error={errors.mobile} hint="10 digits, no country code" innerRef={setFieldRef("mobile")}>
                            <TextInput mono placeholder="98765 43210" maxLength={10} value={data.mobile} onChange={(e) => update("mobile", e.target.value.replace(/\D/g, ""))} error={errors.mobile} />
                        </Field>
                        <Field label="Email address" error={errors.email} innerRef={setFieldRef("email")}>
                            <TextInput type="email" placeholder="you@example.com" value={data.email} onChange={(e) => update("email", e.target.value)} error={errors.email} />
                        </Field>
                    </Row>

                    <Divider label="IDENTITY DOCUMENTS" />

                    <Field label="Aadhar number" error={errors.aadhar} hint="Format: XXXX XXXX XXXX" innerRef={setFieldRef("aadhar")}>
                        {aadharValid && <VerifiedPill />}
                        <TextInput mono placeholder="1234 5678 9012" value={formatAadhar(data.aadhar)} onChange={(e) => update("aadhar", e.target.value.replace(/\D/g, "").slice(0, 12))} error={errors.aadhar} />
                    </Field>
                    <Field label="Upload Aadhar card" error={errors.aadharFile} innerRef={setFieldRef("aadharFile")}>
                        <FileUpload id="aadharFile" file={data.aadharFile} onChange={(f) => update("aadharFile", f)} error={errors.aadharFile} label="Upload front side of Aadhar card" />
                    </Field>

                    <Field label="PAN number" error={errors.pan} hint="Format: ABCDE1234F" innerRef={setFieldRef("pan")}>
                        {panValid && <VerifiedPill />}
                        <TextInput mono placeholder="ABCDE1234F" maxLength={10} value={data.pan} onChange={(e) => update("pan", e.target.value.toUpperCase().slice(0, 10))} error={errors.pan} />
                    </Field>
                    <Field label="Upload PAN card" error={errors.panFile} innerRef={setFieldRef("panFile")}>
                        <FileUpload id="panFile" file={data.panFile} onChange={(f) => update("panFile", f)} error={errors.panFile} label="Upload front side of PAN card" />
                    </Field>

                    <Field label="GST number" error={errors.gst} hint="Format: 22ABCDE1234F1Z5 · optional, for businesses only" innerRef={setFieldRef("gst")}>
                        {data.gst.length > 0 && gstValid && <VerifiedPill />}
                        <TextInput mono placeholder="22ABCDE1234F1Z5" maxLength={15} value={data.gst} onChange={(e) => update("gst", e.target.value.toUpperCase().slice(0, 15))} error={errors.gst} />
                    </Field>
                    <Field label="Upload GST certificate" hint="Optional">
                        <FileUpload id="gstFile" file={data.gstFile} onChange={(f) => update("gstFile", f)} label="Upload GST registration certificate" />
                    </Field>

                    <Divider label="ADDRESS" />

                    <Field label="Address line 1" error={errors.addr1} innerRef={setFieldRef("addr1")}>
                        <TextInput placeholder="House / flat no., building, street" value={data.addr1} onChange={(e) => update("addr1", e.target.value)} error={errors.addr1} />
                    </Field>
                    <Field label="Address line 2" hint="Optional">
                        <TextInput placeholder="Landmark, area" value={data.addr2} onChange={(e) => update("addr2", e.target.value)} />
                    </Field>
                    <Row>
                        <Field label="City" error={errors.city} innerRef={setFieldRef("city")}>
                            <TextInput placeholder="e.g. Hyderabad" value={data.city} onChange={(e) => update("city", e.target.value)} error={errors.city} />
                        </Field>
                        <Field label="State" error={errors.state} innerRef={setFieldRef("state")}>
                            <SelectInput value={data.state} onChange={(e) => update("state", e.target.value)} error={errors.state}>
                                <option value="">Select state</option>
                                {[
                                    "Andhra Pradesh", "Delhi", "Gujarat", "Karnataka", "Kerala",
                                    "Maharashtra", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana",
                                    "Uttar Pradesh", "West Bengal", "Other",
                                ].map((s) => <option key={s} value={s}>{s}</option>)}
                            </SelectInput>
                        </Field>
                    </Row>
                    <Row>
                        <Field label="PIN code" error={errors.pincode} innerRef={setFieldRef("pincode")}>
                            <TextInput mono placeholder="500081" maxLength={6} value={data.pincode} onChange={(e) => update("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} error={errors.pincode} />
                        </Field>
                        <Field label="Country">
                            <TextInput value="India" disabled style={{ ...inputStyle(false, false), opacity: 0.6 }} />
                        </Field>
                    </Row>

                    <Divider label="CONFIRM & SUBMIT" />

                    <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontFamily: SANS, fontSize: "13px", color: T.slate, marginBottom: "18px", cursor: "pointer" }}>
                        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: "2px" }} />
                        I confirm the details above are accurate and match my official documents.
                    </label>
                    <button
                        onClick={handleSubmit}
                        disabled={!agreed}
                        style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                            width: "100%", fontFamily: SANS, fontWeight: 700, fontSize: "14px", color: "#fff",
                            background: agreed ? T.saffron : "#D9CBB5",
                            border: "none", borderRadius: "8px",
                            padding: "14px 22px", cursor: agreed ? "pointer" : "default",
                        }}
                    >
                        Submit registration <Check size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}